import { useLocalStorage } from '@vueuse/core'
import { defineStore } from 'pinia'
import { type Ref, ref } from 'vue'

/**
 * Lets a view ask the page layout for more width than the centered container
 * gives it, for content whose width the user controls — the assets table once
 * enough columns are shown. A route that always wants the full viewport uses
 * `meta.fullWidth` instead.
 */
export const useLayoutStore = defineStore('layout', () => {
  /**
   * How many pixels wider the current view's content is than the space it has
   * right now; negative when it has room to spare. Relative rather than absolute
   * so the view can report it without knowing the layout's own padding, and so it
   * holds steady as the layout widens: the space grows by exactly what this drops.
   *
   * `null` when no view is asking, which is not the same as 0. Relative to the
   * current width, 0 means "fits exactly as it is now" — and once the page is
   * wide, "as it is now" is wide, so a 0 left behind would hold every page wide.
   */
  const contentOverflow: Ref<number | null> = ref(null)

  /** Whether the sidebar is collapsed to icons only. */
  const sidebarCollapsed = useLocalStorage('layout:sidebar-collapsed', false)

  return { contentOverflow, sidebarCollapsed }
})

export default useLayoutStore
